import { Const } from '@/const';
import { ProductCategory } from '@/data/productCategory/productCategory';
import { CreatePostParamHelper } from '@/helper/createPostParamHelper';
import { productSearchInfo } from '@/definitions/productSearchInfo';
import { RegionHelper } from '@/helper/regionHelper';
import parseJSON from '@/helper/parseJson';
import platform from '@/helper/platform';
import { allCategories } from './items';
import { RequestHelper } from '@/helper/requestHelper';

export interface IProductRepository {
    paginate(page: number): Promise<[ProductCategory[], number]>;
}

class SimpleProductRepository implements IProductRepository {
    private readonly categories: ProductCategory[];
    private readonly productsCount: number;

    public constructor(categories: ProductCategory[], productsCount: number) {
        this.categories = categories;
        this.productsCount = productsCount;
    }

    // カテゴリごとに取り出す
    public paginate(page: number): Promise<[ProductCategory[], number]> {
        if (page < 1) throw 'out of index';

        return new Promise(resolve => {
            const i = page - 1;
            if (this.categories.length < page) {
                resolve([[], 0]);
                return;
            }
            resolve([[this.categories[i]], this.productsCount]);
        });
    }
}

function countProducts(categories: ProductCategory[]): number {
    let count = 0;
    categories.forEach(c => {
        count += c.productList.length;
    });
    return count;
}

export class IDBProductRepository {
    public static all(): Promise<IProductRepository> {
        return new Promise(resolve => {
            allCategories(ps => ps).then(categories => {
                const n = countProducts(categories);
                resolve(new SimpleProductRepository(categories, n));
            });
        });
    }

    public static searchWithWord(word: string): Promise<IProductRepository> {
        return new Promise(resolve => {
            allCategories(ps => ps.filter(p => p.code.includes(word) || p.name.includes(word))).then(categories => {
                const n = countProducts(categories);
                resolve(new SimpleProductRepository(categories, n));
            });
        });
    }

    public static searchWithSize(width: number, height: number, range: number): Promise<IProductRepository> {
        const minWidth = width ? width - range : 0;
        const maxWidth = width ? width + range : Infinity;
        const minHeight = height ? height - range : 0;
        const maxHeight = height ? height + range : Infinity;

        return new Promise(resolve => {
            // TODO 整理する
            allCategories(products =>
                products.filter(product => {
                    if (!product.paperList) return false;
                    const pw = product.paperList[0].width;
                    const ph = product.paperList[0].height;
                    return minWidth <= pw && pw <= maxWidth && minHeight <= ph && ph <= maxHeight;
                })
            ).then(categories => {
                const n = countProducts(categories);
                resolve(new SimpleProductRepository(categories, n));
            });
        });
    }
}

export class XHRProductRepository {
    private readonly url = RequestHelper.getApiUrl("get_product_category_list");
    private readonly params: productSearchInfo;
    private lastOneId?: number;
    private lastOneProductCount: number = 0;
    private lastOneSortType: number = 0;

    private constructor(params: productSearchInfo) {
        this.params = params;
    }

    public static all(): XHRProductRepository {
        return new XHRProductRepository({
            width: 0,
            height: 0,
            range: 0,
            searchWord: '',
            scrollNew: false,
            productCategorySortNum: 0,
            productCount: 0,
            platform: platform.id,
            region: RegionHelper.getRegion(),
        });
    }

    public static searchWithWord(word): XHRProductRepository {
        return new XHRProductRepository({
            width: 0,
            height: 0,
            range: 0,
            searchWord: word,
            scrollNew: false,
            productCategorySortNum: 0,
            productCount: 0,
            platform: platform.id,
            region: RegionHelper.getRegion(),
        });
    }

    public static searchWithSize(width, height, range): XHRProductRepository {
        return new XHRProductRepository({
            width: width,
            height: height,
            range: range,
            searchWord: '',
            scrollNew: false,
            productCategorySortNum: 0,
            productCount: 0,
            platform: platform.id,
            region: RegionHelper.getRegion(),
        });
    }

    // ページごとに取得する
    // 1から順に取り出すことしかできない
    public paginate(page: number): Promise<[ProductCategory[], number]> {
        if (page < 1) throw 'out of index';
        if (page === 1) {
            // 1ページ目なら状態を初期化する
            this.lastOneId = undefined;
            this.lastOneProductCount = 0;
            this.lastOneSortType = 0;
        }

        return new Promise((resolve, reject) => {
            const req = new XMLHttpRequest();
            req.onload = () => {
                const response = parseJSON(req.response);
                if (!response || !response.data || !response.data.productCategoryList) {
                    resolve([[], 0]);
                    return;
                }
                const categories: ProductCategory[] = response.data.productCategoryList;
                const amount: number = response.data.totalCount;

                if (categories.length > 0) {
                    // 状態を持っちゃうけどAPIが持ってるので仕方ない
                    const one = categories[categories.length - 1];
                    if (this.lastOneId && this.lastOneId === one.id) {
                        this.lastOneProductCount += one.productList.length;
                    } else {
                        this.lastOneId = one.id;
                        this.lastOneProductCount = one.productList.length;
                    }
                    this.lastOneSortType = one.productCategorySortNum;
                }

                resolve([categories, amount]);
            };
            req.onerror = reject;
            req.onabort = () => {
                resolve([[], 0]);
            };
            req.ontimeout = reject;

            // ページング用のパラメータを設定する
            const params = Object.assign({}, this.params, {
                scrollNew: page > 1,
                productCount: this.lastOneProductCount,
                productCategorySortNum: this.lastOneSortType,
            });

            req.open('POST', this.url);
            req.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
            req.send(CreatePostParamHelper.createPostParam(params));
        });
    }
}
